import { isBrowser } from './browserHelper';
import {
  NATIVE_APP_OPERATION,
  NativeAppParsedQueryParams,
  NativeAppQueryParamKeys,
  getNativeAppOperation,
  getNativeAppQueryParamNames,
  getNativeAppQueryParams,
  isNativeApp,
} from './nativeAppHelper';

export const getPathname = (url: string): string => {
  const clipIndex = process.env.NODE_ENV === 'production' ? 19 : 1;
  return url.substring(clipIndex);
};

export const getServiceUnavailableUrl = () => {
  const currentUrl = window.location.href.slice(0, -1);
  const lastIndex = currentUrl.lastIndexOf('/');
  const path = currentUrl.substring(0, lastIndex);
  return isNativeApp()
    ? `${path}/${getNativeAppOperation()}/service-unavailable`
    : `${path}/service-unavailable`;
};

const BUSINESS_OPERATIONS = [
  'business',
  'businessdesignconsultation',
  'businessinteriordesign',
  'businessnetwork',
  'businessremote',
  'businesshome',
  'businesstore',
  'businesstips',
  'ibn',
  'ibnconsultation',
  'athomeservices',
  'business_id_service',
  'benefit_space_solution',
  'benefit_free_space_consultation',
  'benefit_free_space_consultation_remote',
  'initialmeetingbusiness',
  'coworkerbookedservicesbusiness',
];

const OPERATIONS = [
  NATIVE_APP_OPERATION,
  'advice',
  'bathroom',
  'bathroomstore',
  'bedroom',
  'buyback',
  'childroom',
  'childrensroom',
  'cleanenergy',
  'consultation',
  'coworkerbookedservices',
  'coworkerbookedserviceskitchen',
  'design',
  'designbasic',
  'designcomplete',
  'designcustomer',
  'designhome',
  'designremote',
  'designstore',
  'finalization',
  'finance',
  'fleamarket',
  'flex',
  'homecoach',
  'homeplanning',
  'homesmart',
  'ifpricetest',
  'initialmeeting',
  'internal',
  'internaluseonly',
  'interiordesignvalidation',
  'kitchen',
  'kitchen_eec_dda_aed_bdf',
  'kitchen_sf_trial',
  'kitchenbusiness',
  'kitchenfinalizeremote',
  'kitchenfinalizestore',
  'kitchenfollowup',
  'kitcheninternal',
  'kitchenplanning',
  'kitchenremote',
  'kitchenstore',
  'livingroom',
  'mattress',
  'measurea',
  'measureb',
  'measure_experiment', // test operation area for RS market
  'organize',
  'pax',
  'planningathome',
  'rcmp',
  'recycle',
  'remoteservices',
  'remotemeasurement',
  'reservation',
  'seasonal',
  'shopper',
  'signlanguagesupport',
  'sleep',
  'smarthome',
  'sofa',
  'storage',
  'trailer',
  'verification',
  'workspace',
];

const validPaths = [
  'cancel',
  'questions',
  'service-unavailable',
  'contact',
  'questions-confirmed',
];

const EXTERNAL_QUERY_PARAMS = {
  VPC: 'vpc',
  PROJECT_ID: 'projectid',
};

const BOKA_QUERY_PARAMS = {
  TOKEN: 'token',
  ZIP: 'zip',
  SERVICE: 'service',
};

const isPathValid = (pathName: string): boolean => {
  return validPaths.includes(pathName);
};

export const isBusinessOperation = (operation: string): boolean => {
  return BUSINESS_OPERATIONS.includes(operation);
};

const isValidBokaOperation = (operation: string): boolean => {
  return OPERATIONS.includes(operation) || isBusinessOperation(operation);
};

export const isValidRequest = (url: string, zip?: string): boolean => {
  const [pathName, subPathName] = getPathname(url).split('/');
  if (pathName.length === 0) return true;
  if (isPathValid(pathName) || isPathValid(subPathName)) return true;

  const isValidOperation = isValidBokaOperation(pathName);
  if (isValidOperation && subPathName && !zip) return false;

  return isValidOperation;
};

export const shouldRedirect = (url: string, zip?: string): boolean => {
  return !isValidRequest(url, zip);
};

interface ParsedQueryParams extends NativeAppParsedQueryParams {
  questionsToken: string;
  zip: string;
  service: string;
  vpc: string;
  projectId: string;
}

export const getQueryParamsFromURL = (): ParsedQueryParams => {
  const { SERVICE, ZIP, TOKEN } = BOKA_QUERY_PARAMS;
  const { VPC, PROJECT_ID } = EXTERNAL_QUERY_PARAMS;
  const queryString = isBrowser() ? window.location.search : '';
  const queryParams = new URLSearchParams(queryString);

  const questionsToken = queryParams.get(TOKEN) || '';
  const zip = queryParams.get(ZIP) || '';
  const service = queryParams.get(SERVICE) || '';
  const vpc = queryParams.get(VPC) || '';
  const projectId = queryParams.get(PROJECT_ID) || '';
  const nativeAppParams = getNativeAppQueryParams();

  return { questionsToken, zip, service, vpc, projectId, ...nativeAppParams };
};

type PersistingQueryParamKeys = 'vpc' | 'projectid' | NativeAppQueryParamKeys;

const filterQueryParams = (
  queryParams: URLSearchParams,
  exclude: PersistingQueryParamKeys[],
) => {
  const keys = [...queryParams.keys()];
  const persistingQueryParams = [
    ...Object.values(EXTERNAL_QUERY_PARAMS),
    ...getNativeAppQueryParamNames(),
  ];

  keys.forEach((key) => {
    if (
      !persistingQueryParams.includes(key) ||
      (exclude as string[]).includes(key)
    ) {
      queryParams.delete(key);
    }
  });
};

type AddQueryParamsToPathOptions = {
  addToExistingQueryParam?: boolean;
  exclude?: PersistingQueryParamKeys[];
};

export const addPersistingQueryParamsToPath = (
  path: string,
  options?: AddQueryParamsToPathOptions,
): string => {
  if (!isBrowser()) {
    return path;
  }

  const { addToExistingQueryParam = false, exclude = [] } = options || {};
  const queryParams = new URLSearchParams(window.location.search);
  filterQueryParams(queryParams, exclude);
  const queryParamsString = queryParams.toString();

  return queryParamsString
    ? `${path}${addToExistingQueryParam ? '&' : '?'}${queryParamsString}`
    : path;
};
